"use client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import {
  headerCta,
  isNavDropdown,
  navItems,
  type CategoriesPanel,
  type ProductsPanel,
} from "../../data";
import RingBulletItem from "@/components/common/ringBulletItem/ringBulletItem.component";
import ViewAllLink from "../viewAllLink/viewAllLink.component";
import IconTile from "@/components/common/iconTile/iconTile.component";

interface MobileNavDrawerProps {
  pathname: string;
  // Header is still over a coloured hero - the burger switches to white.
  isOnHero: boolean;
}

// The hamburger menu (below lg): a full-height drawer under the header with
// one accordion per dropdown. Solutions/Resources categories get their own
// expand toggle next to the category link, so the link itself still
// navigates.
const MobileNavDrawer = ({ pathname, isOnHero }: MobileNavDrawerProps) => {
  const initialState = {
    // The path the drawer was opened on ("" = closed). Deriving `isOpen`
    // from it closes the drawer on any route change - browser back/forward
    // included - without a setState-in-effect.
    openedOn: "",
    openGroup: "",
    openCategory: "",
  };
  const [state, setState] = useState(initialState);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const isOpen = state.openedOn === pathname;

  const close = () => setState(initialState);

  // While open: lock the page behind the drawer and let Escape close it,
  // returning focus to the burger so keyboard users aren't stranded.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setState(initialState);
      burgerRef.current?.focus();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const linkProps = (route: string) => ({
    href: route,
    onClick: close,
    "aria-current": pathname === route ? ("page" as const) : undefined,
  });

  const renderProducts = (panel: ProductsPanel) => (
    <>
      <ul className="grid gap-0.5">
        {panel.links.map((link, index) => (
          <li key={link.route}>
            <Link
              {...linkProps(link.route)}
              className="grid grid-cols-[44px_1fr] items-center gap-3.5 rounded-xl px-1 py-2.5 text-ink aria-[current=page]:text-primary"
            >
              {link.icon && <IconTile icon={link.icon} index={index} />}
              <span>
                <span className="block text-base leading-tight font-semibold">
                  {link.label}
                </span>
                <span className="mt-0.5 block text-[13.5px] leading-snug text-ink-muted">
                  {link.description}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {panel.allLink && (
        <ViewAllLink link={panel.allLink} onClick={close} className="mt-1 px-1" />
      )}

      <div className="mt-2 border-t border-line pt-3">
        <span className="mb-2 ml-1 block text-sm font-semibold text-ink-muted">
          {panel.gridLabel}
        </span>
        <ul className="grid grid-cols-1 gap-1 min-[420px]:grid-cols-2">
          {panel.gridLinks.map((link, index) => (
            <li key={link.route}>
              <Link
                {...linkProps(link.route)}
                className="flex items-center gap-3 rounded-xl px-1 py-2 text-base leading-tight font-medium text-ink aria-[current=page]:font-semibold aria-[current=page]:text-primary"
              >
                {link.icon && (
                  <IconTile icon={link.icon} index={index} size="sm" />
                )}
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-3 grid gap-1.5 rounded-2xl promo-band px-3.5 py-3">
        <Link
          {...linkProps(panel.promo.route)}
          className="text-lg leading-tight font-extrabold text-ink"
        >
          {panel.promo.title}
        </Link>
        <span className="text-[12.5px] leading-snug font-medium text-ink-muted">
          {panel.promo.description}
        </span>
        <Link
          {...linkProps(panel.promo.cta.route)}
          className="mt-1 rounded-full bg-primary px-4 py-2.5 text-center text-sm font-bold text-white hover:bg-primary/90"
        >
          {panel.promo.cta.label}
        </Link>
      </div>
    </>
  );

  const renderCategories = (panel: CategoriesPanel, groupLabel: string) => (
    <>
      <ul>
        {panel.categories.map((category, index) => {
          const key = `${groupLabel}-${category.label}`;
          const detailId = `mobile-nav-${key.replace(/\W+/g, "-")}`;
          const isOpen = state.openCategory === key;
          const toggle = () =>
            setState((state) => ({
              ...state,
              openCategory: isOpen ? "" : key,
            }));
          const rowContent = (
            <>
              <IconTile icon={category.icon} index={index} />
              <span>
                <span
                  className={cn(
                    "block text-base leading-tight font-semibold",
                    isOpen && "text-primary",
                  )}
                >
                  {category.label}
                </span>
                <span className="mt-0.5 block text-[13.5px] leading-snug text-ink-muted">
                  {category.description}
                </span>
              </span>
            </>
          );
          const rowClass =
            "grid grid-cols-[44px_1fr] items-center gap-3.5 px-1 py-2.5 text-left text-ink";

          return (
            <li
              key={category.label}
              className="grid grid-cols-[1fr_auto] items-center border-b border-line last:border-b-0"
            >
              {category.route ? (
                <Link {...linkProps(category.route)} className={rowClass}>
                  {rowContent}
                </Link>
              ) : (
                <button type="button" className={rowClass} onClick={toggle}>
                  {rowContent}
                </button>
              )}
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={detailId}
                aria-label={`Show ${category.label} details`}
                className="grid size-11 place-items-center text-ink"
                onClick={toggle}
              >
                <ChevronDown
                  className={cn(
                    "size-[18px] transition-transform duration-150",
                    isOpen && "rotate-180",
                  )}
                  aria-hidden="true"
                />
              </button>

              <div
                id={detailId}
                role="group"
                aria-label={category.label}
                className={cn(
                  "col-span-2 pb-2.5 pl-2",
                  isOpen ? "block" : "hidden",
                )}
              >
                {category.links && (
                  <ul className="grid gap-1">
                    {category.links.map((link, linkIndex) => (
                      <li key={link.route}>
                        <Link
                          {...linkProps(link.route)}
                          className="flex items-center gap-3 rounded-xl px-1 py-2 text-base leading-tight font-medium text-ink aria-[current=page]:font-semibold aria-[current=page]:text-primary"
                        >
                          {link.icon && (
                            <IconTile
                              icon={link.icon}
                              index={linkIndex}
                              size="sm"
                            />
                          )}
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                {(category.posts || category.notes) && (
                  <ul className="grid gap-2 py-1">
                    {category.posts?.map((post) => (
                      <RingBulletItem key={post.route}>
                        <Link
                          {...linkProps(post.route)}
                          className="text-[15px] leading-snug font-medium text-ink hover:text-primary hover:underline"
                        >
                          {post.label}
                        </Link>
                      </RingBulletItem>
                    ))}
                    {category.notes?.map((note) => (
                      <RingBulletItem
                        key={note}
                        className="text-[15px] leading-snug text-ink"
                      >
                        {note}
                      </RingBulletItem>
                    ))}
                  </ul>
                )}
                {category.route && category.exploreLabel && (
                  <Link
                    {...linkProps(category.route)}
                    className="mt-2.5 inline-block text-[15px] font-semibold text-primary underline underline-offset-[3px]"
                  >
                    {category.exploreLabel}
                  </Link>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {panel.allLink && (
        <ViewAllLink link={panel.allLink} onClick={close} className="mt-2 px-1" />
      )}
      {panel.footerLink && (
        <Link
          {...linkProps(panel.footerLink.route)}
          className="mt-2.5 flex items-center justify-between gap-2.5 rounded-2xl promo-band px-3.5 py-2.5 text-[14.5px] font-bold text-ink"
        >
          {panel.footerLink.label}
          <ArrowRight className="size-5 shrink-0 text-primary" aria-hidden="true" />
        </Link>
      )}
    </>
  );

  return (
    <div className="ml-auto lg:hidden">
      <button
        ref={burgerRef}
        type="button"
        aria-label={isOpen ? "Close menu" : "Open menu"}
        aria-expanded={isOpen}
        aria-controls="mobile-nav"
        className={cn(
          "grid size-11 place-items-center rounded-[10px] border transition-colors duration-300",
          isOnHero ? "border-white/30 text-white" : "border-line text-ink",
        )}
        onClick={() =>
          setState(isOpen ? initialState : { ...initialState, openedOn: pathname })
        }
      >
        {isOpen ? (
          <X className="size-[22px]" aria-hidden="true" />
        ) : (
          <Menu className="size-[22px]" aria-hidden="true" />
        )}
      </button>

      {isOpen && (
        <nav
          id="mobile-nav"
          aria-label="Main"
          className="fixed inset-x-0 top-[72px] bottom-0 z-50 flex flex-col overflow-y-auto overscroll-contain bg-white px-6 pt-3 pb-8"
        >
          <Accordion
            type="single"
            collapsible
            value={state.openGroup}
            onValueChange={(openGroup) =>
              setState((state) => ({ ...state, openGroup, openCategory: "" }))
            }
          >
            {navItems.map((item) =>
              isNavDropdown(item) ? (
                <AccordionItem
                  key={item.label}
                  value={item.label}
                  className="border-line last:border-b"
                >
                  <AccordionTrigger className="items-center rounded-none px-1 py-4 text-lg font-medium text-ink hover:no-underline">
                    {item.label}
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    {item.panel.kind === "products"
                      ? renderProducts(item.panel)
                      : renderCategories(item.panel, item.label)}
                  </AccordionContent>
                </AccordionItem>
              ) : (
                <Link
                  key={item.route}
                  {...linkProps(item.route)}
                  className="block border-b border-line px-1 py-4 text-lg font-medium text-ink aria-[current=page]:text-primary"
                >
                  {item.label}
                </Link>
              ),
            )}
          </Accordion>

          <Button
            asChild
            variant="brand"
            className="mt-6 h-auto w-full rounded-full py-[15px] text-base"
          >
            <Link href={headerCta.route} onClick={close}>
              {headerCta.label}
            </Link>
          </Button>
        </nav>
      )}
    </div>
  );
};

export default MobileNavDrawer;
